import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'

import { getActionbyIncident } from '../../redux/actions/actionActions'
import {
  mapIncidentToAudits,
  calculateIncidentActionsCosts,
  getIncidentbyId,
} from '../../redux/incidents/incidentActions'
import { AppDispatch, useAppDispatch, RootState } from '../../redux/store'
import BarChart from '../../components/common/BarChart'
import { hasChartData } from '../../components/common/PieChart'
import {
  AnalysisChartCard,
  AnalysisKpiCard,
  AnalysisPageLoader,
  AnalysisSection,
} from '../../components/common/analysisLayout'
import {
  DetailButton,
  DetailPageHead,
} from '../../components/common/detail'
import {
  AnalysisPdfButton,
  useAnalysisPdfExport,
} from '../../components/common/AnalysisPdfButton'
import AnalysisPdfReport from '../../Pdf/AnalysisPdfReport'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { analysisColorPalette } from '../../utils/colors'
import { formatCurrency, locale } from '../../utils/formatNumbers'
import '../../components/common/maturity-analysis.css'

function IncidentAnalysis() {
  const dispatch: AppDispatch = useAppDispatch()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const incident = useSelector((state: RootState) => state.incident.incident)
  const { audits, totalCost } = useSelector(
    (state: RootState) => state.incident
  )
  const { id } = useParams<{ id: string }>()
  const currency = useSelector(selectSpecifications)[0]?.Currency
  const pdf = useAnalysisPdfExport()
  const [loading, setLoading] = useState(true)
  const COLORS = analysisColorPalette

  React.useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!id) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        await Promise.all([
          dispatch(mapIncidentToAudits(id)),
          dispatch(calculateIncidentActionsCosts(id)),
          dispatch(getIncidentbyId(id)),
          dispatch(getActionbyIncident(id)),
        ])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [dispatch, id])

  const computedOn = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [i18n.language]
  )

  const barChartData = {
    labels: (audits || []).map((audit) => audit?.AuditName || '—'),
    datasets: [
      {
        label: t('charts.auditOverview.datasetLabel'),
        data: (audits || []).map((audit) => audit?.Description?.length || 0),
        backgroundColor: (audits || []).map(
          (_, i) => COLORS[i % COLORS.length]
        ),
        borderColor: (audits || []).map((_, i) => COLORS[i % COLORS.length]),
        borderWidth: 0,
      },
    ],
  }

  const costValue =
    totalCost === undefined || totalCost === null
      ? '—'
      : formatCurrency(Number(totalCost) || 0, currency || '', {
          locale,
          useIntl: true,
        })

  const backPath = id ? `/incident-detail/${id}` : '/incidents'

  return (
    <div className="maturity-analysis-page detail-page">
      <DetailPageHead
        eyebrow={t('analysis.incidentAnalysis.eyebrow', {
          defaultValue: 'Operations · Incidents',
        })}
        title={t('analysis.incidentAnalysis.pageTitle', {
          defaultValue: 'Incident Analysis',
        })}
        subtitle={t('analysis.incidentAnalysis.subtitle', {
          name: incident?.IncidentName || '—',
          date: computedOn,
          defaultValue: `Indicators for ${incident?.IncidentName || 'this incident'}, computed on ${computedOn}.`,
        })}
        actions={
          <>
            <DetailButton variant="ghost" onClick={() => navigate(backPath)}>
              <ArrowBackOutlined sx={{ fontSize: 16 }} />
              {t('analysis.incidentAnalysis.backToIncident', {
                defaultValue: 'Back to incident',
              })}
            </DetailButton>
            <AnalysisPdfButton
              exporting={pdf.exporting}
              disabled={loading}
              onExport={() =>
                pdf.exportPdf(incident?.IncidentName || 'incident-analysis')
              }
            />
          </>
        }
      />

      {loading ? (
        <AnalysisPageLoader kpiCount={1} chartCount={1} />
      ) : (
        <>
          <div className="ma-kpi-grid">
            <AnalysisKpiCard
              label={t('charts.actionCosts.title')}
              value={costValue}
              tone={Number(totalCost) > 0 ? 'teal' : 'muted'}
              stampTitle={computedOn}
              description={t('analysis.incidentAnalysis.costDesc', {
                defaultValue: 'Total cost of actions linked to this incident.',
              })}
            />
          </div>

          <AnalysisSection
            label={t('analysis.incidentAnalysis.sectionOverview', {
              defaultValue: 'Linked audits',
            })}
          >
            <AnalysisChartCard
              title={t('charts.auditOverview.title')}
              hasData={hasChartData(barChartData)}
            >
              <BarChart
                embedded
                title={t('charts.auditOverview.title')}
                chartTitle={t('charts.auditOverview.chartTitle')}
                data={barChartData}
              />
            </AnalysisChartCard>
          </AnalysisSection>
        </>
      )}

      <div className="ma-pdf-print-host" aria-hidden>
        <AnalysisPdfReport
          ref={pdf.ref}
          eyebrow={t('analysis.incidentAnalysis.eyebrow')}
          title={incident?.IncidentName || '—'}
          subtitle={t('analysis.incidentAnalysis.subtitle', {
            name: incident?.IncidentName || '—',
            date: computedOn,
          })}
          footerTitle={t('analysis.incidentAnalysis.pageTitle')}
          computedOn={computedOn}
          kpis={[
            {
              label: t('charts.actionCosts.title'),
              value: costValue,
              desc: t('analysis.incidentAnalysis.costDesc'),
              tone: Number(totalCost) > 0 ? 'teal' : 'muted',
            },
          ]}
          sections={[
            {
              label: t('analysis.incidentAnalysis.sectionOverview'),
              charts: [
                {
                  key: 'audits',
                  title: t('charts.auditOverview.title'),
                  kind: 'bar',
                  data: barChartData,
                },
              ],
            },
          ]}
        />
      </div>
    </div>
  )
}

export default IncidentAnalysis
